import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { AuthContext } from "../../../context/AuthContext";
import { SnackAlertContext } from "../../../context/SnackAlertContext";
import TimeSummary from "../../shop/Time/TimeSummary";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import useFileUpload from "../../../hooks/useFileUpload";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // ES 2015
import timezone from "dayjs/plugin/timezone"; // ES 2015
import isoWeek from "dayjs/plugin/isoWeek"; // ES 2015
import ReOpenWeeklyTimeSummaryDialog from "./ReOpenWeeklyTimeSummaryDialog";
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
const DEFAULT_TIMEZONE = "America/Phoenix";
dayjs.tz.setDefault(DEFAULT_TIMEZONE);

pdfMake.vfs = pdfFonts.pdfMake.vfs;

// isoDate is "YYYY-MM-DD"
// the isoDateParam will always be treated as a Phoenix Date
const WeeklyTimeSummary = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { ezorder, authToken, decoded, role } = useContext(AuthContext);
  const { openSnackMessage } = useContext(SnackAlertContext);
  const [loading, setLoading] = useState(false);
  const [weeklyUserTimeSummary, setWeeklyUserTimeSummary] = useState();
  const [reOpenSummaryDialogOpen, setReOpenSummaryDialogOpen] = useState(false);

  const { uploadFile } = useFileUpload(ezorder, authToken);

  const handleFileUpload = async (e) => {
    try {
      setLoading(true);
      const file = e.target.files[0];
      // We are uploadng as an admin for a shop employee
      let identifier = `SHOP-EMPLOYEE-${weeklyUserTimeSummary.user.id}`;
      const key = await uploadFile(
        identifier,
        "manually-signed-time-summaries",
        file
      );
      console.log("File Uploaded to", key);
      approveTimeWithManualUpload(key);
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const getTimeSummary = async () => {
    try {
      setLoading(true);

      let response = await ezorder.get(`/admin/user/time-summaries/${id}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      if (response.status == 200 || response.status === 201) {
        setWeeklyUserTimeSummary(response.data.weeklyUserTimeSummary);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const approveTime = async (pin) => {
    try {
      setLoading(true);

      let response = await ezorder.post(
        `/user/time-summaries/${id}/approve`,
        { pin },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status == 200 || response.status === 201) {
        openSnackMessage("success", "Time Card Signed");
        getTimeSummary();
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const approveTimeWithManualUpload = async (s3Key) => {
    try {
      setLoading(true);

      let response = await ezorder.post(
        `/user/time-summaries/${id}/approve-by-upload`,
        { uploadedSignedSummary: s3Key },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status == 200 || response.status === 201) {
        openSnackMessage("success", "Time Card Signed");
        getTimeSummary();
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const requestNewPin = async () => {
    try {
      setLoading(true);

      let response = await ezorder.post(
        `/auth/pin`,
        {},
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status == 200 || response.status === 201) {
        openSnackMessage("success", "A new PIN Number was sent to your phone");
      } else {
        openSnackMessage("error", response.response.data.error);
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const createPresignedUrl = async (s3Key) => {
    try {
      let data = {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      };

      let keySplit = s3Key.split("/");
      let identifier = keySplit[0];
      let resource = keySplit[1];
      let fileName = keySplit[2];
      const response = await ezorder.post(
        "/files/presignedUrl",
        {
          identifier,
          resource,
          fileName,
          actionType: "READ",
          contentType: "application/pdf",
        },
        data
      );
      return response.data.getUrl;
    } catch (error) {
      // console.log(error);
      throw Error("Unable to load presigned URL", { cause: error });
    }
  };

  const downloadUploadedSignedSummary = async () => {
    let presignedUrl = await createPresignedUrl(
      weeklyUserTimeSummary.uploadedSignedSummary
    );

    // using Java Script method to get PDF file
    fetch(presignedUrl).then((response) => {
      response.blob().then((blob) => {
        console.log("BLOB", blob);
        // Creating new object of PDF file
        const fileURL = window.URL.createObjectURL(blob);
        // Setting various property values
        let alink = document.createElement("a");
        alink.href = fileURL;
        let fileExtension =
          weeklyUserTimeSummary.uploadedSignedSummary.split(".")[1];
        let fileName = `WEEKLY_TIME_SUMMARY_${dayjs(weeklyUserTimeSummary.start)
          .tz(DEFAULT_TIMEZONE)
          .startOf("isoWeek")
          .format("YYYY-MM-DD")} - ${dayjs(weeklyUserTimeSummary.start)
          .tz(DEFAULT_TIMEZONE)
          .endOf("isoWeek")
          .format("YYYY-MM-DD")}.${fileExtension}`;
        alink.download = fileName;
        alink.click();
      });
    });
  };

  const reOpenWeeklyTimeSummary = async () => {
    try {
      setLoading(true);

      let response = await ezorder.patch(
        `/reopen-weekly-user-time-summary/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.status == 200 || response.status === 201) {
        openSnackMessage("success", "Weekly Summary Reopened For Editing");
        setReOpenSummaryDialogOpen(false);
        getTimeSummary();
      }
    } catch (error) {
      console.log(error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.log(error.response.data);
        console.log(error.response.status);
        console.log(error.response.headers);
        openSnackMessage("error", error.response.data.error);
      } else {
        // Something went really bad
        console.log(error);
        openSnackMessage("error", error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTimeSummary();
  }, [id]);

  if (!weeklyUserTimeSummary) return null;

  return (
    <>
      <TimeSummary
        isAdmin={role === "EZORDER_ADMIN"}
        weeklyUserTimeSummary={weeklyUserTimeSummary}
        approveTime={approveTime}
        requestNewPin={requestNewPin}
        enableSignWithPin={
          // ONLY ALLOW ADMIN USER TO SIGN WITH PIN IF IT IS THEIR OWN
          decoded.id == weeklyUserTimeSummary.user.id
        }
        loading={loading}
        enableUploadSignedTimeSummary={true}
        handleFileUpload={handleFileUpload}
        downloadUploadedSignedSummary={downloadUploadedSignedSummary}
        onReOpen={() => {
          setReOpenSummaryDialogOpen(true);
        }}
      />
      <ReOpenWeeklyTimeSummaryDialog
        open={reOpenSummaryDialogOpen}
        handleClose={() => {
          setReOpenSummaryDialogOpen(false);
        }}
        onConfirm={reOpenWeeklyTimeSummary}
        loading={loading}
      />
    </>
  );
};

export default WeeklyTimeSummary;
